import { Component } from '@theme/component';

/**
 * @typedef {import('@theme/component').Refs & {
 *   panel: HTMLDivElement;
 *   field: HTMLDivElement;
 *   orderInput: HTMLInputElement;
 *   submit: HTMLButtonElement;
 *   captcha: HTMLDivElement;
 *   validation: HTMLParagraphElement;
 *   results: HTMLDivElement;
 *   card: HTMLElement;
 * }} Refs
 */

const ENDPOINT_BASE = 'https://apimanagement.gco.com.co:8444/customer/api/tracking/selfservice/';
const BRAND_CODE = '34';
const FETCH_TIMEOUT_MS = 10000;
/** The customer types only the number; the block supplies the prefix. */
const ORDER_PATTERN = /^\d{1,20}$/;
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const CHALLENGE_SELECTOR = 'iframe[src*="/recaptcha/"][src*="bframe"]';

/** Comfortably longer than `--animation-speed` (0.125s) so the fade completes. */
const CLOSE_DELAY_MS = 250;

/** Progress ordering of the API statuses, used to place the current step. */
const DELIVERY_ORDER = ['ready-for-handling', 'handling', 'invoiced', 'transit', 'in-distribution', 'delivered'];
const PICKUP_ORDER = ['ready-for-handling', 'handling', 'invoiced', 'pickup', 'pickup-invoiced', 'delivered'];

/** Detail fields that hold a date and need locale formatting before display. */
const DATE_FIELDS = new Set(['lastUpdate', 'expectedDate']);

/** Result-card colour per status; anything not listed reads as success. */
const CARD_TONES = { canceled: 'danger', unknown: 'neutral' };

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Drives the tracking modal rendered by `blocks/_tracking-link.liquid`.
 *
 * All markup and copy live in the block; this component owns behaviour only:
 * the overlay lifecycle, the reCAPTCHA v2 gate, the tracking API request, and
 * swapping between the views the block already rendered (`[data-view]`,
 * `[data-status]`) while filling the `[data-value]` slots with the response.
 *
 * This is deliberately NOT a `<dialog>`. `showModal()` marks everything outside
 * the dialog as `inert`, and reCAPTCHA injects its image challenge into
 * `<body>` — so the challenge would render but refuse clicks. A plain overlay
 * keeps the challenge interactive.
 *
 * @extends Component<Refs>
 */
export class OrderTrackingComponent extends Component {
  requiredRefs = ['panel', 'field', 'orderInput', 'submit', 'captcha', 'validation', 'results', 'card'];

  connectedCallback() {
    // The block renders inside the sticky header, which is a stacking context
    // (`z-index: 8`), and the overlay has to sit above the whole page. Moving
    // the element re-runs this callback; the second pass, now parented to
    // <body>, does the actual initialisation.
    if (this.parentElement !== document.body) {
      this.portal();
      return;
    }

    super.connectedCallback();

    this.siteKey = this.getAttribute('data-recaptcha-site-key') ?? '';
    this.orderPrefix = this.getAttribute('data-order-prefix') ?? '';

    this.orderValue = this.refs.card.querySelector('[data-value="order"]');
    this.detailRows = this.refs.card.querySelectorAll('[data-field]');
    this.timelines = this.refs.results.querySelectorAll('[data-flow]');

    // The block enumerates every status it can label, so the set of statuses we
    // can render is derived from the markup instead of duplicated here.
    this.knownStatuses = new Set(
      Array.from(this.refs.card.querySelectorAll('.tracking-link-block__status-title [data-status]')).flatMap((node) =>
        node.dataset.status.split(' ')
      )
    );

    this.isOpen = false;
    this.closeTimer = null;
    this.previousFocus = null;
    this.scrollY = 0;
    this.abortController = null;
    this.timeoutId = null;
    this.widgetId = null;
    this.token = '';
    this.captchaFailed = false;

    this.trigger = document.querySelector(`[data-tracking-trigger="${this.dataset.trackingModal}"]`);
    this.trigger?.addEventListener('click', this.open);

    this.ready = true;
  }

  disconnectedCallback() {
    // The first pass bailed out to portal the element, so there is nothing to
    // tear down yet.
    if (!this.ready) return;

    super.disconnectedCallback();
    this.abortLookup();
    clearTimeout(this.timeoutId);
    clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.handleKeydown);
    this.trigger?.removeEventListener('click', this.open);
    this.releaseScrollLock();
  }

  portal() {
    const id = this.dataset.trackingModal;

    // A theme-editor re-render builds a fresh block, so drop the copy the
    // previous render parked in <body>.
    for (const stale of document.querySelectorAll(`body > [data-tracking-modal="${id}"]`)) {
      if (stale !== this) stale.remove();
    }

    document.body.appendChild(this);
  }

  /* -------------------------------------------------------------- lifecycle */

  open = () => {
    if (this.isOpen) return;
    this.isOpen = true;

    clearTimeout(this.closeTimer);
    this.previousFocus = document.activeElement;
    this.lockScroll();

    this.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.handleKeydown);

    // Let the browser paint the closed state once so the transition runs.
    requestAnimationFrame(() => this.classList.add('is-open'));

    this.refs.panel.focus({ preventScroll: true });
    this.setupCaptcha();
  };

  close = () => {
    if (!this.isOpen) return;
    this.isOpen = false;

    document.removeEventListener('keydown', this.handleKeydown);
    this.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');

    this.abortLookup();
    // The challenge lives outside the overlay, so it would otherwise stay on
    // screen after the modal is gone. Resetting also drops a token that would
    // be stale by the next open.
    this.resetCaptcha();
    this.updateSubmitGate();

    this.closeTimer = setTimeout(() => {
      this.hidden = true;
    }, CLOSE_DELAY_MS);

    this.releaseScrollLock();
    if (this.previousFocus instanceof HTMLElement) this.previousFocus.focus();
  };

  lockScroll() {
    this.scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
  }

  releaseScrollLock() {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.scrollY, behavior: 'instant' });
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;
    // While the challenge is up, focus has to be able to leave the panel and
    // reach Google's iframe.
    if (isChallengeOpen()) return;

    const focusable = Array.from(this.refs.panel.querySelectorAll(FOCUSABLE)).filter(
      (node) => node.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------------------ views */

  /** Shows the nodes the block tagged with this view and hides the rest. */
  setView(view) {
    this.dataset.view = view;
    for (const node of this.querySelectorAll('[data-view]')) {
      node.hidden = !node.dataset.view.split(' ').includes(view);
    }
  }

  /** Same idea for the status-specific copy inside the result card. */
  setStatus(status) {
    for (const node of this.refs.card.querySelectorAll('[data-status]')) {
      node.hidden = !node.dataset.status.split(' ').includes(status);
    }
  }

  /* -------------------------------------------------------------- reCAPTCHA */

  async setupCaptcha() {
    if (this.widgetId !== null || this.captchaFailed) return;
    if (!this.siteKey) {
      this.renderCaptchaError();
      return;
    }

    try {
      const grecaptcha = await loadRecaptchaScript();
      this.renderWidget(grecaptcha);
    } catch (_error) {
      this.renderCaptchaError();
    }
  }

  renderWidget(grecaptcha) {
    if (this.widgetId !== null || this.captchaFailed) return;
    try {
      this.widgetId = grecaptcha.render(this.refs.captcha, {
        sitekey: this.siteKey,
        callback: () => {
          this.token = grecaptcha.getResponse(this.widgetId) || '';
          this.clearValidation();
          this.updateSubmitGate();
        },
        'expired-callback': () => {
          this.token = '';
          this.showValidation('El reCAPTCHA expiró. Verifícalo nuevamente.');
          this.updateSubmitGate();
        },
        'error-callback': () => {
          this.token = '';
          this.showValidation('No se pudo verificar el reCAPTCHA. Intenta nuevamente.');
          this.updateSubmitGate();
        },
      });
      this.updateSubmitGate();
    } catch (_error) {
      this.renderCaptchaError();
    }
  }

  renderCaptchaError() {
    this.captchaFailed = true;
    const message = document.createElement('p');
    message.className = 'tracking-link-block__captcha-error';
    message.textContent = 'No se pudo cargar el reCAPTCHA. Verifica tu conexión e intenta nuevamente.';
    this.refs.captcha.replaceChildren(message);
    this.refs.submit.disabled = true;
  }

  resetCaptcha() {
    try {
      if (this.widgetId !== null && typeof window.grecaptcha?.reset === 'function') {
        window.grecaptcha.reset(this.widgetId);
      }
    } catch (_error) {
      /* no-op: widget reset is best-effort */
    }
    this.token = '';
  }

  /* -------------------------------------------------------------- form gate */

  updateSubmitGate = () => {
    if (this.captchaFailed) {
      this.refs.submit.disabled = true;
      return;
    }
    const hasOrder = this.refs.orderInput.value.trim().length > 0;
    this.refs.submit.disabled = !(hasOrder && this.token);
  };

  handleInput = () => {
    this.clearValidation();
    this.refs.field.classList.remove('is-invalid');
    this.refs.orderInput.removeAttribute('aria-invalid');
    if (this.dataset.view !== 'idle') this.setView('idle');
    this.updateSubmitGate();
  };

  /**
   * Customers are shown the prefix as a fixed affix and type only the number,
   * but plenty will paste the whole thing anyway — so a pasted prefix (and any
   * separator around it) is stripped rather than rejected.
   */
  readOrderNumber() {
    let value = this.refs.orderInput.value.trim().replace(/\s+/g, '');
    // Compared without its separator, so `ESC-1092`, `ESC1092` and `esc 1092`
    // all reduce to the same number.
    const prefix = this.orderPrefix.replace(/[^a-z0-9]/gi, '');

    if (prefix && value.toUpperCase().startsWith(prefix.toUpperCase())) {
      value = value.slice(prefix.length);
    }
    return value.replace(/^[-\s]+/, '');
  }

  handleSubmit = (event) => {
    event.preventDefault();
    if (this.captchaFailed) return;

    const number = this.readOrderNumber();
    if (!ORDER_PATTERN.test(number)) {
      this.showValidation('Ingresa solo el número del pedido, sin letras ni guiones.');
      this.refs.orderInput.focus();
      return;
    }
    if (!this.token) {
      this.showValidation('Completa el reCAPTCHA para continuar.');
      return;
    }

    // Keep the field showing exactly what will be queried.
    this.refs.orderInput.value = number;
    this.clearValidation();
    this.lookup(this.orderPrefix + number);
  };

  reset = () => {
    this.abortLookup();

    this.refs.orderInput.value = '';
    this.token = '';
    this.refs.field.classList.remove('is-invalid');
    this.refs.orderInput.removeAttribute('aria-invalid');

    this.clearValidation();
    this.setView('idle');
    this.resetCaptcha();
    this.updateSubmitGate();

    this.refs.orderInput.focus();
  };

  /* ------------------------------------------------------------------ fetch */

  abortLookup() {
    this.abortController?.abort();
    this.abortController = null;
    clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }

  async lookup(order) {
    this.abortLookup();

    this.setView('loading');
    this.refs.results.setAttribute('aria-busy', 'true');
    this.refs.submit.disabled = true;

    const controller = new AbortController();
    this.abortController = controller;
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    this.timeoutId = timeoutId;

    const url = `${ENDPOINT_BASE}${BRAND_CODE}/${encodeURIComponent(order)}`;

    let result = null;
    let outcome = null;

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });

      if (response.status === 404) {
        outcome = 'not-found';
      } else if (!response.ok) {
        outcome = 'error';
      } else {
        let data;
        try {
          data = await response.json();
        } catch (_error) {
          outcome = 'error';
        }
        if (outcome === null) {
          result = this.normalize(data);
          if (!result) {
            // Non-array payload, or a status the block cannot label: the
            // response contract was not met, so fall back to the generic error.
            outcome = 'error';
          } else if (result.type === 'empty') {
            outcome = 'not-found';
          } else {
            outcome = 'found';
          }
        }
      }
    } catch (_error) {
      // Superseded by a newer lookup or a reset: abandon quietly and leave the
      // UI to the active request.
      if (this.abortController !== controller) return;
      // Timeout (AbortError) or network/CORS (TypeError): generic error.
      outcome = 'error';
    }

    // Superseded while awaiting: do not mutate the active request's UI.
    if (this.abortController !== controller) return;

    clearTimeout(timeoutId);
    this.abortController = null;
    this.resetCaptcha();
    this.refs.results.setAttribute('aria-busy', 'false');

    if (outcome === 'found') {
      this.renderFound(order, result);
    } else {
      this.renderFailure(outcome);
    }

    this.updateSubmitGate();
  }

  /**
   * Normalizes the API payload. The self-service endpoint always returns an
   * array of status entries; the latest entry carries the metadata shown in the
   * detail rows. Non-array payloads violate the contract and return `null`; an
   * empty array, or one without usable statuses, maps to not-found.
   */
  normalize(data) {
    if (!Array.isArray(data)) return null;

    const entries = data.filter((item) => item && typeof item === 'object').map(toEntry);

    if (entries.length === 0) return { type: 'empty' };

    const tracked = entries.filter((entry) => entry.status);

    // The API can acknowledge an order it has no tracking status for yet — it
    // answers 200 with the order id and dates but `status: null`. That is a
    // known order, not a missing one, so it gets a card without a timeline
    // instead of the "not found" view.
    if (tracked.length === 0) return { type: 'status', status: 'unknown', entry: entries[entries.length - 1] };

    const statuses = tracked.map((entry) => entry.status);
    const current = statuses[statuses.length - 1];
    const currentEntry = tracked[tracked.length - 1];

    if (statuses.includes('canceled')) {
      const canceled = tracked.find((entry) => entry.status === 'canceled') ?? currentEntry;
      return { type: 'status', status: 'canceled', entry: canceled };
    }

    if (!this.knownStatuses.has(current)) return null;

    const isPickup = current === 'pickup' || current === 'pickup-invoiced';
    const orderList = isPickup ? PICKUP_ORDER : DELIVERY_ORDER;

    // `order-created` (and anything else outside the progress list) has no
    // timeline position, so it renders as a status card only.
    if (!orderList.includes(current)) return { type: 'status', status: current, entry: currentEntry };

    return { type: 'timeline', status: current, isPickup, entry: currentEntry };
  }

  /* -------------------------------------------------------------- rendering */

  renderFound(order, result) {
    this.refs.card.dataset.tone = CARD_TONES[result.status] ?? 'success';
    if (this.orderValue) this.orderValue.textContent = order;

    this.setStatus(result.status);
    this.fillDetails(result.entry);
    this.markTimeline(result);
    this.setView('found');
  }

  fillDetails(entry) {
    for (const row of this.detailRows) {
      const field = row.dataset.field;
      const raw = entry[field] ?? '';
      const value = DATE_FIELDS.has(field) ? formatDate(raw) : raw;

      const slot = row.querySelector('[data-value]');
      if (slot) slot.textContent = value;
      row.hidden = !value;
    }
  }

  markTimeline(result) {
    const flow = result.isPickup ? 'pickup' : 'delivery';

    for (const list of this.timelines) {
      const active = result.type === 'timeline' && list.dataset.flow === flow;
      list.hidden = !active;
      if (!active) continue;

      const steps = Array.from(list.children);
      const currentIndex = steps.findIndex((step) => step.dataset.match.split(' ').includes(result.status));

      steps.forEach((step, index) => {
        step.classList.toggle('is-done', index < currentIndex);
        step.classList.toggle('is-current', index === currentIndex);
        // An empty `aria-current` maps to "false", so it has to be set or removed.
        if (index === currentIndex) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
    }
  }

  /** Keeps the form on screen with the typed number so it can be fixed in place. */
  renderFailure(view) {
    this.setView(view);
    this.refs.field.classList.add('is-invalid');
    this.refs.orderInput.setAttribute('aria-invalid', 'true');
    this.refs.orderInput.focus();
    this.refs.orderInput.select();
  }

  showValidation(message) {
    this.refs.validation.textContent = message;
  }

  clearValidation() {
    this.refs.validation.textContent = '';
  }
}

if (!customElements.get('order-tracking-component')) {
  customElements.define('order-tracking-component', OrderTrackingComponent);
}

/** True while reCAPTCHA is showing its image challenge. */
function isChallengeOpen() {
  for (const frame of document.querySelectorAll(CHALLENGE_SELECTOR)) {
    const container = frame.closest('body > div');
    if (container && container.style.visibility !== 'hidden') return true;
  }
  return false;
}

/** Reads one API item, defaulting every field to a string. */
function toEntry(item) {
  const text = (value) => (typeof value === 'string' ? value : '');

  return {
    orderId: text(item.orderId),
    status: text(item.status),
    lastUpdate: text(item.lastUpdate),
    expectedDate: text(item.expectedDate),
    trackingNumber: text(item.trackingNumber),
    courierName: text(item.courierName),
  };
}

/** Formats `YYYY-MM-DD`, `YYYY-MM-DD HH:mm:ss` or ISO as a Colombian long date. */
function formatDate(value) {
  if (!value) return '';

  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value.replace(' ', 'T');
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/**
 * Loads the reCAPTCHA explicit script exactly once across all component
 * instances and resolves with the global `grecaptcha` object.
 *
 * @returns {Promise<typeof window.grecaptcha>}
 */
function loadRecaptchaScript() {
  if (typeof window.grecaptcha?.render === 'function') {
    return Promise.resolve(window.grecaptcha);
  }

  if (loadRecaptchaScript.promise) return loadRecaptchaScript.promise;

  loadRecaptchaScript.promise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${RECAPTCHA_SRC}"]`);
    const script = existing ?? document.createElement('script');

    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };

    const onLoad = () => {
      const grecaptcha = window.grecaptcha;
      if (typeof grecaptcha?.render === 'function') {
        cleanup();
        resolve(grecaptcha);
      } else if (typeof grecaptcha?.ready === 'function') {
        grecaptcha.ready(() => {
          cleanup();
          resolve(grecaptcha);
        });
      } else {
        cleanup();
        reject(new Error('reCAPTCHA unavailable'));
      }
    };

    const onError = () => {
      cleanup();
      reject(new Error('reCAPTCHA script failed to load'));
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    if (!existing) {
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadRecaptchaScript.promise;
}
